import axios from 'axios';

const API_URL = '/api/auth/';
const API_BASE_URL = import.meta.env.VITE_API_URL || '';

const authService = {
  login: async (email, password) => {
    try {
      const response = await axios.post(API_URL + 'login/', {
        email,
        password,
      });

      if (response.data.token) {
        // Store both access and refresh tokens
        const userData = {
          token: response.data.token,
          refresh: response.data.refresh,
          email: response.data.user.email,
          id: response.data.user.id,
        };
        localStorage.setItem('user', JSON.stringify(userData));

        await axios
          .get(`/api/set-csrf-cookie/`, { withCredentials: true })
          .then((response) => {
            const csrfToken = response.data.csrfToken; // Extract CSRF token from response
            if (csrfToken) {
              localStorage.setItem('csrfToken', csrfToken); // Save to local storage
            }
          })
          .catch((error) => {
            console.error('Error setting CSRF cookie:', error);
          });
      }

      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'An error occurred during login' };
    }
  },

  logout: () => {
    localStorage.removeItem('user');
  },

  getCurrentUser: () => {
    const userStr = localStorage.getItem('user');
    return userStr ? JSON.parse(userStr) : null;
  },

  // Register new user
  register: async (email, password) => {
    try {
      const response = await axios.post(API_URL + 'register/', {
        email,
        password,
      });
      return response.data;
    } catch (error) {
      throw (
        error.response?.data || {
          message: 'An error occurred during registration',
        }
      );
    }
  },

  // Request password reset
  requestPasswordReset: async (email) => {
    try {
      const response = await axios.post(API_URL + 'password-reset/', {
        email,
      });
      return response.data;
    } catch (error) {
      throw (
        error.response?.data || {
          message: 'An error occurred requesting password reset',
        }
      );
    }
  },

  // Refresh the access token using the refresh token
  refreshToken: async () => {
    try {
      const user = authService.getCurrentUser();
      if (!user?.refresh) {
        throw new Error('No refresh token available');
      }

      const response = await axios.post(API_URL + 'token/refresh/', {
        refresh: user.refresh,
      });

      if (response.data.access) {
        // Update the stored user data with the new access token
        const updatedUser = {
          ...user,
          token: response.data.access,
        };
        localStorage.setItem('user', JSON.stringify(updatedUser));
        return updatedUser;
      }
    } catch (error) {
      throw error.response?.data || { message: 'Failed to refresh token' };
    }
  },
};

// Add axios interceptor to handle token
axios.interceptors.request.use(
  (config) => {
    const user = authService.getCurrentUser();
    if (user && user.token) {
      config.headers.Authorization = `Bearer ${user.token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

export default authService;
